import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, CardTitle, Input, Form, FormGroup, Label, Button } from 'reactstrap';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { useApiClient } from '../../api/client.jsx';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { usageFixture } from './fixture';
import { createDebugLogger } from '../../utils/debug';

const dlog = createDebugLogger('usage');
const fmtNum = new Intl.NumberFormat('de-DE');

function fmtYmd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function UsagePage() {
  const [userId, setUserId] = useState('');
  const [eventType, setEventType] = useState('');
  const [from, setFrom] = useState(() => fmtYmd(new Date(Date.now() - 14 * 86_400_000)));
  const [to, setTo] = useState(() => fmtYmd(new Date()));

  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  const query = useQuery({
    queryKey: ['usage', { userId, eventType, from, to }, fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return usageFixture({ userId, eventType, from, to });
      }
      const qs = new URLSearchParams();
      if (userId) qs.set('userId', userId);
      if (eventType) qs.set('eventType', eventType);
      // Core erwartet ISO-8601 Datetime (Stand 2026-06-13: YMD allein → 400
      // validation_error). Wir extendieren YMD auf vollen Tag in UTC.
      if (from) qs.set('from', new Date(`${from}T00:00:00.000Z`).toISOString());
      if (to) qs.set('to', new Date(`${to}T23:59:59.999Z`).toISOString());
      const res = await fetch(`/v1/admin/usage?${qs.toString()}`);
      dlog('/v1/admin/usage raw', res);
      return res;
    },
  });

  const data = query.data;
  const chartData = data?.aggregate?.byEventType
    ? Object.entries(data.aggregate.byEventType).map(([type, info]) => ({
        type,
        total: info.total,
        unit: info.unit,
      }))
    : [];

  return (
    <div>
      <PageHeader
        title="Usage"
        subtitle="Event-Tracking · GET /v1/admin/usage"
      />

      <Card className="mb-3">
        <CardBody className="py-3">
          <Form className="d-flex flex-wrap gap-2 align-items-end">
            <FormGroup className="mb-0">
              <Label className="small">User-ID</Label>
              <Input
                bsSize="sm"
                value={userId}
                onChange={(e) => setUserId(e.target.value.replace(/\D/g, ''))}
                placeholder="z. B. 4711"
                style={{ maxWidth: 130 }}
              />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="small">Event-Type</Label>
              <Input
                type="select"
                bsSize="sm"
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                style={{ minWidth: 200 }}
              >
                <option value="">Alle</option>
                {(data?.knownEventTypes ?? []).map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="small">Von</Label>
              <Input type="date" bsSize="sm" value={from} onChange={(e) => setFrom(e.target.value)} />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="small">Bis</Label>
              <Input type="date" bsSize="sm" value={to} onChange={(e) => setTo(e.target.value)} />
            </FormGroup>
            {(userId || eventType) && (
              <Button color="link" size="sm" onClick={() => { setUserId(''); setEventType(''); }}>
                Filter leeren
              </Button>
            )}
          </Form>
        </CardBody>
      </Card>

      {chartData.length > 0 && (
        <Card className="mb-3">
          <CardBody>
            <CardTitle tag="h5">Aggregate pro Event-Type</CardTitle>
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="type" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                  <Tooltip formatter={(v, _name, ctx) => `${fmtNum.format(v)} ${ctx.payload.unit}`} />
                  <Legend />
                  <Bar dataKey="total" name="Summe" fill="#3f6ad8" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>
      )}

      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!data?.events?.length}
        emptyMessage="Keine Usage-Events für die aktuellen Filter."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th>Event-Type</th>
            <th style={{ width: 100 }}>User-ID</th>
            <th style={{ width: 100 }} className="text-end">Count</th>
            <th style={{ width: 80 }}>Unit</th>
            <th style={{ width: 150 }}>Wann</th>
          </tr>
        </thead>
        <tbody>
          {data?.events?.map((e, i) => (
            <tr key={i}>
              <td><code style={{ fontSize: 12 }}>{e.eventType}</code></td>
              <td>#{e.userId}</td>
              <td className="text-end fw-semibold">{fmtNum.format(e.count)}</td>
              <td className="small text-muted">{e.unit}</td>
              <td><RelativeTime iso={e.at} /></td>
            </tr>
          ))}
        </tbody>
      </DataTable>

      {data?.aggregate && (
        <p className="text-muted small mt-2 mb-0">
          {data.events?.length ?? 0} Events angezeigt (max 200) ·
          Gesamt-Aggregate: {fmtNum.format(data.aggregate.total)}
        </p>
      )}
    </div>
  );
}
